//@ts-check
/**
 * Captures that have not reached the server yet, kept on the device until they do.
 *
 * Standing in a shop with one bar, or none, a want has to be as safe as a photo in the camera
 * roll — which works in airplane mode. So a capture is written here *before* it is sent, and only
 * removed once the server has it. If the app is closed, killed or offline in between, the entry
 * is still here on the next start and is sent then.
 *
 * Storage only: what to send, when, and what the list shows is `$lib/things.svelte.js`. Entries
 * are keyed by the thing's id, a ULID minted on the phone at capture time, so reading them back in
 * key order is oldest first — the order they were captured in — and a replay of one the server
 * already has lands on the same record rather than a second one.
 *
 * IndexedDB rather than localStorage: it holds the photo as a Blob, and it is the only client-side
 * store the architecture allows (guides/ARCH.md).
 */
import { openDB } from "idb";

const DB_NAME = "tingthings-outbox";
const STORE = "captures";

/**
 * @typedef {object} OutboxEntry
 * @property {string} thingId   minted on the phone; also the key
 * @property {string} owner     the signed-in email it was captured under — only ever sent as them
 * @property {string} text      the capture box, as typed
 * @property {Blob | null} photo the prepared JPEG, until it has uploaded
 * @property {string} imageKey  set once the photo is up, so a retry never uploads it twice
 * @property {any} location     a fix, when the owner asked for one and it arrived
 * @property {number} queuedAt  when it was captured, ms
 * @property {number} attempts  sends tried so far
 * @property {boolean} [details] false when the owner turned _Get details from the page_ off
 * @property {string[]} [listIds] the lists it was captured into — filed by the server when it lands
 */

/** @type {Promise<import("idb").IDBPDatabase> | null} */
let dbp = null;

function db() {
  dbp ??= openDB(DB_NAME, 1, {
    upgrade(d) {
      d.createObjectStore(STORE, { keyPath: "thingId" });
    },
  });
  return dbp;
}

/** @param {OutboxEntry} entry */
export async function putEntry(entry) {
  await (await db()).put(STORE, entry);
}

/** @returns {Promise<OutboxEntry[]>} oldest first */
export async function allEntries() {
  return (await db()).getAll(STORE);
}

/** @param {string} thingId */
export async function removeEntry(thingId) {
  await (await db()).delete(STORE, thingId);
}

/** Forget every waiting capture, photos and all: for an account that has just been deleted. */
export async function clearOutbox() {
  await (await db()).clear(STORE);
}

/**
 * Ask the browser not to clear this site's storage under pressure. Best effort, and asked only
 * once something is actually waiting: an unsent capture is the one thing here that cannot be
 * fetched again.
 */
export function askToPersist() {
  navigator.storage?.persist?.().catch(() => {});
}
